import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown, Info, Save } from "lucide-react";
import { useToast } from "../../Moduels/Context/ToastContext";
import { fetchEncashmentSettings, saveEncashmentSettings } from "../../Moduels/leave/encashmentApi";
import "./LeaveEncashmentSettings.css";
import { useNavigate } from "react-router-dom";
import { navigateToSettingsHome } from "./settingsExit";

const METHODS = [
  {
    value: "year_end",
    title: "Year-End Encashment Only",
    description: "Year-end processing runs in the selected month. Employees can still raise a request for HR, admin, or super admin approval.",
  },
  {
    value: "employee_request",
    title: "Employee Request Only",
    description: "Employees can submit a request for encashment.",
  },
  {
    value: "both",
    title: "Both (Year-End & Employee Request)",
    description: "Year-end encashment and employee request both will be allowed.",
  },
];

const MANAGER_OPTIONS = [
  { value: "required", title: "Required", description: "Request will go to reporting manager." },
  { value: "not_required", title: "Not Required", description: "Request will skip reporting manager approval." },
];

const HR_OPTIONS = [
  { value: "required", title: "Required", description: "Request will go to HR for approval." },
  { value: "not_required", title: "Not Required", description: "Request will skip HR approval." },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const STEPS = [
  "Enable leave encashment and configure the settings.",
  "Employees must satisfy the minimum EL balance requirement.",
  "Employees submit encashment requests, or year-end processing considers eligible employees, depending on the selected method.",
  "An employee request waits for HR, admin, or super admin approval. Approval creates the leave encashment.",
  "Approved encashment records are sent to the existing Payroll process.",
  "Payroll calculates the applicable amount and displays Leave Encashment as a separate earning line.",
  "The corresponding EL balance is deducted exactly once according to the established transaction workflow.",
];

function formFrom(data) {
  return {
    enabled: Boolean(data.enabled),
    method: data.method || "both",
    min_el_balance: data.min_el_balance == null ? "" : String(data.min_el_balance),
    min_days: data.min_days == null ? "" : String(data.min_days),
    year_end_month: Number(data.year_end_month || 3),
    manager_approval_required: Boolean(data.manager_approval_required),
    hr_approval_required: Boolean(data.hr_approval_required),
  };
}

function OptionSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const listId = useId();
  const selected = options.find((item) => String(item.value) === String(value)) || options[0];

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const choose = (next) => {
    onChange(next);
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActive((current) => {
        const delta = event.key === "ArrowDown" ? 1 : -1;
        return (current + delta + options.length) % options.length;
      });
    } else if (event.key === "Enter" && open) {
      event.preventDefault();
      choose(options[active].value);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="le-select" ref={ref}>
      <button
        type="button"
        className="le-select-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={onKeyDown}
      >
        <span>{selected?.title}</span>
        <ChevronDown size={16} aria-hidden />
      </button>
      {open ? (
        <ul className="le-select-menu" id={listId} role="listbox" aria-label={label}>
          {options.map((option, index) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={String(option.value) === String(value)}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(option.value)}
              >
                <strong>{option.title}</strong>
                {option.description ? <span>{option.description}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function validate(form) {
  const errors = {};
  const balance = Number(form.min_el_balance);
  const days = Number(form.min_days);
  if (form.min_el_balance === "" || Number.isNaN(balance) || balance < 0 || balance > 365) {
    errors.min_el_balance = "Enter a minimum EL balance from 0 to 365 days.";
  }
  if (form.min_days === "" || Number.isNaN(days) || days <= 0 || days > 365) {
    errors.min_days = "Enter minimum days greater than 0 and at most 365.";
  }
  if (!METHODS.some((item) => item.value === form.method)) {
    errors.method = "Choose an encashment method.";
  }
  if (form.year_end_month < 1 || form.year_end_month > 12) {
    errors.year_end_month = "Choose a month.";
  }
  return errors;
}

export default function LeaveEncashmentSettingsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [errors, setErrors] = useState({});
  const [showHow, setShowHow] = useState(false);
  const howRef = useRef(null);

  useEffect(() => {
    if (!showHow) return undefined;
    const onPointer = (event) => {
      if (howRef.current && !howRef.current.contains(event.target)) setShowHow(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setShowHow(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [showHow]);

  const load = () => {
    setLoading(true);
    setLoadError("");
    fetchEncashmentSettings()
      .then((data) => {
        setForm(formFrom(data));
      })
      .catch((error) => setLoadError(error.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const patch = (next) => setForm((current) => ({ ...current, ...next }));
  const showYearEnd = form && (form.method === "year_end" || form.method === "both");

  const onSave = async () => {
    if (!form || saving) return;
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setSaving(true);
    try {
      const saved = await saveEncashmentSettings({
        enabled: form.enabled,
        method: form.method,
        min_el_balance: Number(form.min_el_balance),
        min_days: Number(form.min_days),
        year_end_month: Number(form.year_end_month),
        manager_approval_required: form.manager_approval_required,
        hr_approval_required: form.hr_approval_required,
      });
      setForm(formFrom(saved));
      toast.success("Leave encashment settings were saved.");
      navigateToSettingsHome(navigate, { replace: true });
    } catch (error) {
      toast.error(error.message || "Save failed. Your entries are still on the form.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="le-page">
      <header className="le-head">
        <h1>Leave Encashment</h1>
        <div className="le-head-actions" ref={howRef}>
          <button
            type="button"
            className={showHow ? "le-how-btn open" : "le-how-btn"}
            aria-expanded={showHow}
            aria-controls="le-how-panel"
            onClick={() => setShowHow((current) => !current)}
          >
            <Info size={16} aria-hidden />
            How Leave Encashment Works
            <ChevronDown className="le-how-chevron" size={16} aria-hidden />
          </button>
          <button type="button" className="le-save" onClick={onSave} disabled={saving || loading || !form}>
            <Save size={16} aria-hidden />
            {saving ? "Saving…" : "Save Settings"}
          </button>
          {showHow ? (
            <section className="le-info" id="le-how-panel" aria-labelledby="le-how">
              <h2 id="le-how">How Leave Encashment Works</h2>
              <ol className="le-steps">
                {STEPS.map((step, index) => (
                  <li key={step}><b>{index + 1}</b><span>{step}</span></li>
                ))}
              </ol>
            </section>
          ) : null}
        </div>
      </header>

      {loadError ? (
        <div className="le-banner" role="alert">
          {loadError}
          <button type="button" className="le-save" style={{ marginTop: 10 }} onClick={load}>Try again</button>
        </div>
      ) : null}

      {loading ? <p className="le-state">Loading encashment settings…</p> : null}

      {form ? (
        <form className="le-stack" onSubmit={(event) => { event.preventDefault(); onSave(); }}>
          <section className="le-card" aria-labelledby="le-policy">
            <header className="le-card-head">
              <h2 id="le-policy">Policy</h2>
            </header>
            <div className="le-row">
              <div className="le-copy">
                <h3>Enable Leave Encashment</h3>
                <p>Turn on to enable Earned Leave encashment for your organization.</p>
              </div>
              <div className="le-switch-wrap">
                <button
                  type="button"
                  className={form.enabled ? "le-switch on" : "le-switch"}
                  role="switch"
                  aria-checked={form.enabled}
                  aria-label="Enable Leave Encashment"
                  onClick={() => patch({ enabled: !form.enabled })}
                >
                  <span />
                </button>
                <span className="le-switch-label">{form.enabled ? "Enabled" : "Disabled"}</span>
              </div>
            </div>

            <div className="le-row">
              <div className="le-copy">
                <h3>Encashment Method</h3>
                <p>Choose when leave encashment can be done.</p>
              </div>
              <OptionSelect
                label="Encashment Method"
                value={form.method}
                options={METHODS}
                onChange={(method) => patch({ method })}
              />
            </div>
          </section>

          <section className="le-card" aria-labelledby="le-eligibility">
            <header className="le-card-head">
              <h2 id="le-eligibility">Eligibility</h2>
            </header>
            <div className="le-row">
              <div className="le-copy">
                <h3>Minimum EL Balance Required</h3>
                <p>Minimum earned leave balance an employee must have to be eligible for encashment.</p>
                {errors.min_el_balance ? <p className="le-error">{errors.min_el_balance}</p> : null}
              </div>
              <label className="le-number">
                <input
                  type="number"
                  min="0"
                  max="365"
                  step="0.5"
                  inputMode="decimal"
                  aria-label="Minimum EL balance required"
                  value={form.min_el_balance}
                  onChange={(event) => patch({ min_el_balance: event.target.value })}
                />
                <span>days</span>
              </label>
            </div>

            <div className="le-row">
              <div className="le-copy">
                <h3>Minimum Days to Encash</h3>
                <p>Minimum number of EL days allowed per encashment request.</p>
                {errors.min_days ? <p className="le-error">{errors.min_days}</p> : null}
              </div>
              <label className="le-number">
                <input
                  type="number"
                  min="0.5"
                  max="365"
                  step="0.5"
                  inputMode="decimal"
                  aria-label="Minimum days to encash"
                  value={form.min_days}
                  onChange={(event) => patch({ min_days: event.target.value })}
                />
                <span>days</span>
              </label>
            </div>

            {showYearEnd ? (
              <div className="le-row">
                <div className="le-copy">
                  <h3>Year-End Encashment</h3>
                  <p>Month in which year-end encashment will be processed.</p>
                </div>
                <OptionSelect
                  label="Year-end encashment month"
                  value={form.year_end_month}
                  options={MONTHS.map((title, index) => ({ value: index + 1, title }))}
                  onChange={(year_end_month) => patch({ year_end_month: Number(year_end_month) })}
                />
              </div>
            ) : null}
          </section>

          <section className="le-card" aria-labelledby="le-approval">
            <header className="le-card-head">
              <h2 id="le-approval">Approval</h2>
            </header>
            <div className="le-row">
              <div className="le-copy">
                <h3>Reporting Manager Approval</h3>
                <p>
                  {form.manager_approval_required
                    ? "Request will go to the reporting manager."
                    : "Request will skip reporting manager approval."}
                </p>
              </div>
              <OptionSelect
                label="Reporting Manager Approval"
                value={form.manager_approval_required ? "required" : "not_required"}
                options={MANAGER_OPTIONS}
                onChange={(value) => patch({ manager_approval_required: value === "required" })}
              />
            </div>

            <div className="le-row">
              <div className="le-copy">
                <h3>HR Approval</h3>
                <p>
                  {form.hr_approval_required
                    ? "Request will go to HR for approval."
                    : "Request will skip HR approval."}
                </p>
              </div>
              <OptionSelect
                label="HR Approval"
                value={form.hr_approval_required ? "required" : "not_required"}
                options={HR_OPTIONS}
                onChange={(value) => patch({ hr_approval_required: value === "required" })}
              />
            </div>
          </section>
        </form>
      ) : null}
    </div>
  );
}
